import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV, downloadXLSX } from '@/lib/exports';
import { ExternalLink, FileText, CheckCircle2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

const STATUS_PILL = {
  paid: { color: '#7BC47F', label: 'Paid' },
  pending: { color: '#E0A659', label: 'Pending' },
  failed: { color: '#E0573C', label: 'Failed' },
  refunded: { color: '#8A6FCB', label: 'Refunded' },
};

const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium' }); } catch { return iso; } };
const inr = (paise) => paise ? `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

const AdminPaymentsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [q, setQ] = useState('');
  const [markPaidRow, setMarkPaidRow] = useState(null);
  // Bulk selection — payments are keyed by the underlying `entry_id`
  // (nomination id). Deleting one "payment row" removes the linked
  // nomination via /admin/bulk-delete resource=payments.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await ax.get(`${API}/admin/payments`); setItems(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load payments.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (filter !== 'all') out = out.filter((r) => r.status === filter);
    if (modeFilter !== 'all') out = out.filter((r) => (r.payment_mode || 'razorpay') === modeFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((r) => [r.company, r.contact_person, r.email, r.campaign, r.invoice_number, r.entry_id_short, r.offline_reference].some((v) => String(v || '').toLowerCase().includes(s)));
    }
    return out;
  }, [items, filter, modeFilter, q]);

  const exportRows = filtered.map((r) => ({
    'Entry ID': r.entry_no != null ? String(r.entry_no).padStart(2, '0') : r.entry_id_short, Company: r.company, 'Contact Person': r.contact_person,
    Email: r.email, Campaign: r.campaign, Category: r.category,
    'Invoice #': r.invoice_number || '—', 'GST #': r.gst_number || '—',
    'Amount (INR)': r.amount_paise ? r.amount_paise / 100 : 0, Date: r.date, Status: r.status,
    Mode: r.payment_mode || '—', 'Offline Ref': r.offline_reference || '—',
    'Payment ID': r.payment_id || '—', 'Order ID': r.payment_order_id || '—',
  }));

  const openInvoice = (r) => {
    // Direct-download PDF (falls back to HTML preview on ?html=1)
    const url = `${BACKEND_URL}/api/admin/nominations/${r.entry_id}/invoice.pdf`;
    window.open(url, '_blank', 'noopener');
  };

  /* --- Bulk selection helpers --- */
  const selectedSet = new Set(selectedIds);
  const visibleIds = filtered.map((r) => r.entry_id).filter(Boolean);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedSet.has(id)) && !allVisibleSelected;
  const toggleAllVisible = (checked) => {
    if (checked) {
      const next = new Set(selectedSet);
      visibleIds.forEach((id) => next.add(id));
      setSelectedIds(Array.from(next));
    } else {
      setSelectedIds(selectedIds.filter((id) => !visibleIds.includes(id)));
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(Array.from(next));
  };
  const performBulkDelete = async () => {
    if (!selectedIds.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await ax.post(`${API}/admin/bulk-delete`, {
        resource: 'payments',
        ids: selectedIds,
      });
      toast.success(`${data.deleted} payment record${data.deleted === 1 ? '' : 's'} deleted.`);
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed.');
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div data-testid="admin-payments-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-[var(--ink-muted)] text-sm">All nomination payments — online, offline, paid, pending.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="payments-search" className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm w-56" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="payments-status-filter" className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" style={{ colorScheme: 'dark' }}>
            <option value="all">All statuses</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="refunded">Refunded</option>
          </select>
          <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} data-testid="payments-mode-filter" className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" style={{ colorScheme: 'dark' }}>
            <option value="all">All modes</option><option value="razorpay">Online (Razorpay)</option><option value="offline">Offline</option>
          </select>
          <button onClick={() => downloadCSV(`adaura-payments-${new Date().toISOString().slice(0,10)}.csv`, exportRows)} className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors">CSV</button>
          <button onClick={() => downloadXLSX(`adaura-payments-${new Date().toISOString().slice(0,10)}.xlsx`, exportRows, 'Payments')} className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors">Excel</button>
        </div>
      </div>

      {loading ? <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div> :
       <>
       {selectedIds.length > 0 && (
         <div
           className="sticky top-[64px] z-20 mb-3 flex items-center justify-between gap-4 px-4 py-3 bg-[#3A2418] text-white border border-[#C9A56C]/40"
           data-testid="admin-payments-bulk-bar"
         >
           <div className="flex items-center gap-3">
             <span className="text-[11px] tracking-[0.3em] uppercase text-[#D9B26A]">Selection</span>
             <span className="text-sm font-semibold" data-testid="admin-payments-bulk-count">
               {selectedIds.length} payment{selectedIds.length === 1 ? '' : 's'} selected
             </span>
           </div>
           <div className="flex items-center gap-2">
             <button type="button" onClick={() => setSelectedIds([])}
               className="text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 text-white/80 hover:text-white transition-colors"
               data-testid="admin-payments-bulk-clear">Clear</button>
             <button type="button" onClick={() => setBulkDeleteOpen(true)}
               className="text-[10px] tracking-[0.28em] uppercase px-4 py-1.5 bg-[#8B3A2E] text-white hover:bg-[#a04336] transition-colors font-semibold"
               data-testid="admin-payments-bulk-delete">Delete Selected</button>
           </div>
         </div>
       )}
       <div className="overflow-x-auto border border-[var(--gold-faint)]">
         <table className="w-full text-sm" data-testid="payments-table">
           <thead className="bg-[var(--bg-deep)]/50">
             <tr>
               <th className="w-10 px-3 py-3 text-left">
                 <input
                   type="checkbox"
                   data-testid="admin-payments-select-all"
                   aria-label="Select all payments on this page"
                   checked={allVisibleSelected}
                   ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                   onChange={(e) => toggleAllVisible(e.target.checked)}
                   disabled={filtered.length === 0}
                   className="w-4 h-4 accent-[var(--gold-deep)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                 />
               </th>
               {['Entry ID', 'Company', 'Contact', 'Category / Campaign', 'Mode', 'Invoice #', 'Amount', 'Date', 'Status', 'Actions'].map((h) => (
                 <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">{h}</th>
               ))}
             </tr>
           </thead>
           <tbody>
             {filtered.length === 0 && (
               <tr>
                 <td colSpan={11} className="text-center py-16 text-[var(--ink-muted)]" data-testid="payments-empty">
                   No payment records.
                 </td>
               </tr>
             )}
             {filtered.map((r, i) => {
               const pill = STATUS_PILL[r.status] || STATUS_PILL.pending;
               const mode = r.payment_mode || (r.payment_order_id ? 'razorpay' : '—');
               const canMarkPaid = r.status === 'pending';
               const rowSelected = selectedSet.has(r.entry_id);
               return (
                 <tr key={r.entry_id || i} className={`border-t border-[var(--gold-faint)]/40 hover:bg-[var(--gold-faint)]/10 ${rowSelected ? 'bg-[var(--gold-faint)]/15' : ''}`} data-testid={`payment-row-${i}`}>
                   <td className="w-10 px-3 py-3 align-top">
                     <input
                       type="checkbox"
                       data-testid={`admin-payments-select-${i}`}
                       aria-label={`Select payment ${i + 1}`}
                       checked={!!rowSelected}
                       onChange={() => toggleOne(r.entry_id)}
                       className="w-4 h-4 accent-[var(--gold-deep)] cursor-pointer"
                     />
                   </td>
                   <td className="px-4 py-3 text-[var(--gold)] numeral text-xs font-bold">{r.entry_no != null ? String(r.entry_no).padStart(2, '0') : r.entry_id_short}</td>
                   <td className="px-4 py-3 text-[var(--ink)]">{r.company || '—'}</td>
                   <td className="px-4 py-3 text-[var(--ink-muted)]">{r.contact_person}<br/><span className="text-[10px]">{r.email}</span></td>
                   <td className="px-4 py-3 text-[var(--ink)] text-xs max-w-[220px]">
                     <div className="font-serif text-[13px] text-[var(--ink)] leading-tight break-words" title={r.campaign || ''}>{r.campaign || '—'}</div>
                     {r.category && <div className="text-[10.5px] text-[var(--ink-muted)] mt-0.5 leading-tight break-words" title={r.category}>{r.category}</div>}
                   </td>
                   <td className="px-4 py-3 text-xs uppercase tracking-[0.15em]">
                     <span className={`inline-block px-2 py-1 border ${mode === 'offline' ? 'bg-[#C2613A]/10 text-[#C2613A] border-[#C2613A]/40 font-semibold' : 'bg-[#7BC47F]/10 text-[#5C9F60] border-[#7BC47F]/40'}`}>{mode}</span>
                     {r.offline_reference && <><br/><span className="text-[10px] text-[var(--ink-muted)] normal-case tracking-normal">Ref: {r.offline_reference}</span></>}
                   </td>
                   <td className="px-4 py-3 text-[var(--ink-muted)] text-xs">{r.invoice_number || '—'}</td>
                   <td className="px-4 py-3 text-[var(--ink)]">{inr(r.amount_paise)}</td>
                   <td className="px-4 py-3 text-[var(--ink-muted)] text-xs">{fmtDate(r.date)}</td>
                   <td className="px-4 py-3"><span className="text-[10px] tracking-[0.2em] uppercase border px-2 py-1" style={{ borderColor: pill.color, color: pill.color }}>{pill.label}</span></td>
                   <td className="px-4 py-3 text-right whitespace-nowrap">
                     {r.status === 'paid' && (
                       <button onClick={() => openInvoice(r)} data-testid={`payment-invoice-${i}`} className="inline-flex items-center gap-1 text-[var(--gold)] hover:text-[var(--gold-bright)] text-xs tracking-[0.2em] uppercase mr-3">
                         <FileText size={11} /> Invoice PDF <ExternalLink size={10} />
                       </button>
                     )}
                     {canMarkPaid && (
                       <button onClick={() => setMarkPaidRow(r)} data-testid={`payment-mark-paid-${i}`}
                         className="inline-flex items-center gap-1 border border-[#7BC47F] text-[#7BC47F] hover:bg-[#7BC47F] hover:text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1">
                         <CheckCircle2 size={11} /> Mark as Paid
                       </button>
                     )}
                   </td>
                 </tr>
               );
             })}
           </tbody>
         </table>
       </div>
       </>}

      <div className="text-[var(--ink-muted)]/60 text-[10px] tracking-[0.25em] uppercase mt-6">
        Showing {filtered.length} payments · Total: {inr(filtered.reduce((a, r) => a + (r.amount_paise || 0), 0))}
      </div>

      {/* Bulk delete confirmation */}
      {bulkDeleteOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4 py-8" data-testid="admin-payments-bulk-delete-modal">
          <div className="bg-white border border-[var(--gold-soft)] max-w-md w-full p-6 shadow-[0_30px_80px_-30px_rgba(58,36,24,0.45)]">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#8B3A2E]">Delete Selected</div>
                <h3 className="font-serif text-xl text-[var(--ink)] mt-1">Confirm bulk delete</h3>
              </div>
              <button onClick={() => (bulkDeleting ? null : setBulkDeleteOpen(false))} className="text-[var(--ink-muted)]"><X size={18} /></button>
            </div>
            <p className="text-[var(--ink)] text-sm">
              You are about to permanently delete{' '}
              <strong>{selectedIds.length}</strong>{' '}
              payment record{selectedIds.length === 1 ? '' : 's'} (and the linked nomination) from the database.
              This action cannot be undone.
            </p>
            <p className="text-[var(--ink-muted)] text-xs mt-3">
              Tip: only use this for junk/test entries. Rows with a Razorpay `Paid` status
              should be handled one at a time so any linked receipts are archived first.
            </p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}
                data-testid="admin-payments-bulk-delete-cancel"
                className="flex-1 border border-[var(--gold-soft)] text-[var(--ink)] py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
                Cancel
              </button>
              <button onClick={performBulkDelete} disabled={bulkDeleting}
                data-testid="admin-payments-bulk-delete-confirm"
                className="flex-1 bg-[#8B3A2E] hover:bg-[#a04336] text-white py-2.5 text-[10px] tracking-[0.28em] uppercase font-semibold disabled:opacity-50">
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {markPaidRow && (
        <MarkPaidModal row={markPaidRow} onClose={() => setMarkPaidRow(null)} onSuccess={() => { setMarkPaidRow(null); load(); }} />
      )}
    </div>
  );
};

/* ---- Mark-as-paid modal (offline verification) ---- */
export const MarkPaidModal = ({ row, onClose, onSuccess }) => {
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reference.trim()) { toast.error('Enter a reference number (bank txn ID, UPI ref, cheque #, etc.).'); return; }
    setSaving(true);
    try {
      const { data } = await ax.post(`${API}/admin/nominations/${row.entry_id}/mark-paid`, {
        reference: reference.trim(),
        note: note.trim(),
      });
      toast.success(`${data.updated} nomination${data.updated === 1 ? '' : 's'} marked paid.`);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark as paid.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4 py-8" data-testid="mark-paid-modal">
      <div className="bg-white border border-[var(--gold-soft)] max-w-md w-full p-6 shadow-[0_30px_80px_-30px_rgba(58,36,24,0.45)]">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">Offline Verification</div>
            <h3 className="font-serif text-xl text-[var(--ink)] mt-1">Mark as Paid</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)]" data-testid="mark-paid-close"><X size={18} /></button>
        </div>
        <div className="text-[12.5px] text-[var(--ink)] space-y-1 border-t border-b border-[var(--gold-soft)]/60 py-3 mb-4">
          <div><span className="text-[var(--ink-muted)]">Entry:</span> {row.campaign || '—'} <span className="text-[var(--ink-muted)]">· #{row.entry_no}</span></div>
          <div><span className="text-[var(--ink-muted)]">Company:</span> {row.company || '—'}</div>
          <div><span className="text-[var(--ink-muted)]">Amount:</span> {inr(row.amount_paise)}</div>
          {row.submission_id && (
            <div className="text-[11px] text-[var(--ink-muted)] italic mt-1">
              This is part of a cart submission — all sibling nominations will also be marked paid.
            </div>
          )}
        </div>
        <label className="block text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-1">Payment Reference *</label>
        <input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="mark-paid-reference"
          placeholder="Bank txn ID / UPI ref / Cheque number"
          className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 w-full text-[var(--ink)] text-sm mb-4" />
        <label className="block text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-1">Note (Optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="mark-paid-note"
          placeholder="Any internal note"
          className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 w-full text-[var(--ink)] text-sm mb-6 min-h-[80px]" />
        <div className="flex gap-3">
          <button onClick={onClose} disabled={saving} data-testid="mark-paid-cancel"
            className="flex-1 border border-[var(--gold-soft)] text-[var(--ink)] py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} data-testid="mark-paid-confirm"
            className="flex-1 bg-[var(--walnut)] text-white py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            {saving ? 'Saving…' : 'Confirm Paid'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentsTab;
